/**
 * Renderer-agnostic board model for the DOM VIF boards.
 *
 * Mirrors the shipping CreateJS behaviour in
 * `games/engine/boardGameEngine.ts` and `games/engine/rulesBoardEngine.ts`
 * exactly - this is a port of the rules, not a redesign. Legacy field names
 * are called out so the two implementations can be diffed against each other
 * while both exist.
 */

export interface GridConfig {
  /** Rules board: 3. Block board: 4. */
  rows: number;
  /** Rules board: 3 (`intColumns`). Block board: 4. */
  cols: number;
}

/** Separates the two halves of a stacked equation. Legacy flag 8 datum. */
const STACKED_SEPARATOR = 'P';

/**
 * Splits a stacked equation into its lines.
 *
 * Flag 8 stores two forms of the same fact in one datum, joined by `P`
 * (`'5 - 2 = xP5 - x = 2'`), and the canvas stacks them with
 * `displayFractionWithoutLine`. Everything else is a single line.
 *
 * Note the canvas is currently broken here: the TypeScript port added
 * `.map(Number)` to the split (`games/VIFRulesAddSub.ts:578`) where the
 * original passed the strings through, so it renders `NaN` over `NaN`. This
 * keeps the strings, matching `VIFRulesAddSub.jsfl.ts:1974-1976`.
 */
export function equationLines(equation: string): string[] {
  return equation.split(STACKED_SEPARATOR);
}

/**
 * Flat, row-major board state. `index = row * cols + col`, matching the
 * legacy flattened indexing (`intColumns * row + col`).
 */
export interface BoardState {
  config: GridConfig;
  /** Top half of each cell: the equation shown. Legacy `topEQs` slice. */
  equations: readonly string[];
  /** Correct answer for each cell. Legacy `tEqValues`. */
  expected: readonly string[];
  /** Answer currently sitting in each cell. Legacy `tileOrigAnswers`. */
  placed: readonly string[];
  /** Cells already resolved correctly. Legacy `tileLocked`. */
  locked: readonly boolean[];
  /** Legacy `intCorrect`. */
  correctCount: number;
  /** True once `correctCount === rows * cols`. */
  complete: boolean;
  /**
   * Cell that just took a wrong answer, or `null`. While non-null the board
   * is mid-rejection and rejects all input, matching the legacy
   * `board.enabled = false` during the 800ms fade before `resetGame()`.
   */
  rejectingIndex: number | null;
}

export type BoardAction =
  /**
   * Deal a fresh board. Used both for the initial deal and for the re-deal
   * after a wrong answer - the legacy reset path re-runs `populateProblem`,
   * so a rejected board comes back with newly drawn problems rather than a
   * re-scramble of the same nine.
   */
  | {
      type: 'DEAL';
      equations: readonly string[];
      expected: readonly string[];
      placed: readonly string[];
    }
  /** Drop the answer held in `from` onto `to`. Legacy `tradePiece()`. */
  | { type: 'DROP'; from: number; to: number };
